/** GET /authn/timezones/search — paginated timezone search for account forms. */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"

const TIMEZONE_PAGE_SIZE = 50

function buildSearchUrl(query = "", page = 0, size = TIMEZONE_PAGE_SIZE) {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("size", String(size))
  if (query?.trim()) {
    params.set("q", query.trim())
  }
  return `/authn/timezones/search?${params.toString()}`
}

/** Display label, e.g. "(GMT +05:30) Asia/Calcutta". */
export const formatTimezoneLabel = (tz) => {
  if (tz == null || tz === "") return ""
  if (typeof tz === "string") return tz.trim()
  const name = tz.name?.trim() || tz.timezone?.trim() || ""
  const rawOffset = tz.utcOffset ?? tz.offset ?? tz.gmtOffset
  if (rawOffset != null && String(rawOffset).trim() !== "") {
    const offset = String(rawOffset).trim()
    const gmt = offset.startsWith("+") || offset.startsWith("-") ? offset : `+${offset}`
    return name ? `(GMT ${gmt}) ${name}` : `(GMT ${gmt})`
  }
  return name
}

/** Browser / OS IANA timezone (e.g. Asia/Calcutta). */
export function getSystemIanaTimezone() {
  try {
    if (typeof Intl === "undefined" || !Intl.DateTimeFormat) return ""
    return Intl.DateTimeFormat().resolvedOptions().timeZone?.trim() || ""
  } catch {
    return ""
  }
}

function ianaSearchVariants(iana) {
  const base = String(iana ?? "").trim()
  if (!base) return []
  const variants = [base]
  if (base === "Asia/Calcutta") variants.push("Asia/Kolkata")
  if (base === "Asia/Kolkata") variants.push("Asia/Calcutta")
  return variants
}

/** True when API row matches the system IANA id. */
export function timezoneRowMatchesIana(row, iana) {
  const rowName = String(row?.name ?? row?.timezone ?? "").trim()
  if (!rowName || !iana) return false
  return ianaSearchVariants(iana).some(
    (variant) => rowName.toLowerCase() === variant.toLowerCase(),
  )
}

/** Resolve system default timezone row via search API. */
export async function resolveSystemTimezoneDefault(dispatch) {
  const iana = getSystemIanaTimezone()
  if (!iana) return null

  for (const query of ianaSearchVariants(iana)) {
    try {
      const result = await dispatch(
        searchTimezones({ query, page: 0, size: TIMEZONE_PAGE_SIZE, append: false }),
      ).unwrap()
      const match = (result?.content || []).find((row) => timezoneRowMatchesIana(row, iana))
      if (match?.id != null) {
        return {
          id: match.id,
          text: formatTimezoneLabel(match),
        }
      }
    } catch {
      /* try next variant */
    }
  }

  return { id: null, text: iana }
}

function resolveLastPage(response, page, size, contentLength) {
  if (response?.last != null) {
    const lastFlag = Boolean(response.last)
    if (lastFlag && contentLength >= size) {
      const totalPages = response?.totalPages
      const number = response?.number ?? page
      if (typeof totalPages === "number" && totalPages > number + 1) {
        return false
      }
      const totalElements = response?.totalElements
      if (typeof totalElements === "number" && number * size + contentLength < totalElements) {
        return false
      }
    }
    return lastFlag
  }

  const totalPages = response?.totalPages
  const number = response?.number ?? page
  if (typeof totalPages === "number" && totalPages > 0) {
    return number + 1 >= totalPages
  }

  const totalElements = response?.totalElements
  if (typeof totalElements === "number" && totalElements > 0) {
    return number * size + contentLength >= totalElements
  }

  return contentLength < size
}

function unwrapTimezonePageResponse(response) {
  if (!response || typeof response !== "object") return response
  if (Array.isArray(response)) return response
  if (Array.isArray(response.content)) return response
  if (response.data && typeof response.data === "object") {
    return response.data
  }
  return response
}

function normalizeSearchResponse(response, { query, page, append, force = false, size = TIMEZONE_PAGE_SIZE }) {
  const body = unwrapTimezonePageResponse(response)
  const content = Array.isArray(body) ? body : body?.content || []
  const number = Array.isArray(body) ? page : body?.number ?? page
  const totalPages = Array.isArray(body) ? 1 : body?.totalPages ?? 0
  const totalElements = Array.isArray(body) ? content.length : body?.totalElements ?? 0
  const last = Array.isArray(body)
    ? content.length < size
    : resolveLastPage(body, page, size, content.length)

  return {
    append: Boolean(append),
    force: Boolean(force),
    query,
    page,
    content,
    last,
    totalPages,
    totalElements,
    number,
  }
}

/** Whether more timezone pages can be fetched for the active query. */
export function getTimezoneHasMore(tz) {
  if (!tz) return false

  const page = tz.page ?? 0
  const itemsLength = tz.items?.length ?? 0

  if (itemsLength >= TIMEZONE_PAGE_SIZE) {
    if (tz.last === true) {
      if (typeof tz.totalElements === "number" && tz.totalElements > itemsLength) {
        return true
      }
      if (typeof tz.totalPages === "number" && tz.totalPages > page + 1) {
        return true
      }
      return false
    }
    return true
  }

  if (typeof tz.totalPages === "number" && tz.totalPages > 0) {
    return page + 1 < tz.totalPages
  }

  if (typeof tz.totalElements === "number" && tz.totalElements > 0) {
    return itemsLength < tz.totalElements
  }

  return tz.last === false
}

export const searchTimezones = createAsyncThunk(
  "timezones/search",
  async ({ query = "", page = 0, size = TIMEZONE_PAGE_SIZE, append = false, force = false } = {}, { rejectWithValue }) => {
    try {
      const response = await get(buildSearchUrl(query, page, size))
      return normalizeSearchResponse(response, {
        query: query?.trim() || "",
        page,
        append,
        force: Boolean(force),
        size,
      })
    } catch (error) {
      console.error("Timezones search API error:", error)
      return rejectWithValue(error?.message || "Failed to fetch timezones")
    }
  },
)

/** Load the next timezone page using current Redux query/page (inbox-style pagination). */
export const loadMoreTimezones = createAsyncThunk(
  "timezones/loadMore",
  async (_, { dispatch, getState }) => {
    const tz = getState().timezones
    if (!getTimezoneHasMore(tz) || tz.loading || tz.loadingMore) return null

    const nextPage = (tz.page ?? 0) + 1
    await dispatch(
      searchTimezones({
        query: tz.query ?? "",
        page: nextPage,
        size: TIMEZONE_PAGE_SIZE,
        append: true,
      }),
    ).unwrap()

    return null
  },
)

const emptyListState = () => ({
  items: [],
  query: "",
  page: 0,
  totalPages: 0,
  totalElements: 0,
  last: true,
  loading: false,
  loadingMore: false,
  error: null,
})

const timezonesSlice = createSlice({
  name: "timezones",
  initialState: emptyListState(),
  reducers: {
    clearTimezones: (state) => {
      Object.assign(state, emptyListState())
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchTimezones.pending, (state, action) => {
        const append = Boolean(action.meta.arg?.append)
        if (append) {
          state.loadingMore = true
        } else {
          state.loading = true
          const nextQuery = action.meta.arg?.query?.trim() ?? ""
          if (nextQuery !== (state.query ?? "")) {
            state.items = []
          }
        }
        state.error = null
      })
      .addCase(searchTimezones.fulfilled, (state, action) => {
        const { append, content, last, totalPages, totalElements, number, query, force } = action.payload
        const requestedPage = action.meta.arg?.page ?? 0
        const requestedQuery = action.meta.arg?.query?.trim() ?? ""

        if (append) {
          if (requestedQuery !== (state.query ?? "").trim()) {
            state.loadingMore = false
            return
          }
          if (requestedPage <= (state.page ?? 0)) {
            state.loadingMore = false
            return
          }
          state.items = [...state.items, ...content]
        } else {
          const isStalePageZero =
            !force &&
            requestedPage === 0 &&
            (state.page ?? 0) > 0 &&
            requestedQuery === (state.query ?? "").trim() &&
            (state.items?.length ?? 0) > (content?.length ?? 0)

          if (isStalePageZero) {
            state.loading = false
            return
          }
          state.items = content
        }

        state.loading = false
        state.loadingMore = false
        state.query = query
        state.page = requestedPage
        state.totalPages = totalPages
        state.totalElements = totalElements
        state.last = last
      })
      .addCase(searchTimezones.rejected, (state, action) => {
        state.loading = false
        state.loadingMore = false
        state.error = action.payload
        if (!action.meta.arg?.append) {
          state.items = []
        }
      })
  },
})

export const { clearTimezones } = timezonesSlice.actions
export { TIMEZONE_PAGE_SIZE }
export default timezonesSlice.reducer
